import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { X } from "lucide-react";
import { RETENTION_DAYS } from "@/config/retention";

const DISMISSED_KEY = "retention-banner-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Tells signed-in users their files are deleted after the retention window. */
export function RetentionBanner() {
  const { isLoaded, isSignedIn } = useAuth();
  // Read synchronously on first render so a dismissed banner never flashes in.
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!isLoaded || !isSignedIn || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Storage unavailable: it stays hidden until the next page load.
    }
  };

  return (
    // Equal padding on both sides keeps the text centred and clear of the ×.
    <div className="relative shrink-0 z-40 px-8 sm:px-10 py-1.5 bg-[#f3eee2] border-b border-[rgba(45,49,39,0.06)] text-center text-[11.5px] sm:text-[12px] leading-snug text-[#8a8570] text-balance">
      Your uploads and videos are deleted after {RETENTION_DAYS}&nbsp;days.{" "}
      <Link to="/assets" className="font-semibold text-[#2d3127] underline underline-offset-2">
        Save your favourites from the library.
      </Link>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#b5b0a0] hover:text-[#5a5545] hover:bg-[rgba(45,49,39,0.05)] transition-colors"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
